import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="resizable"
// Allows users to resize an element from top-left corner and persists the size to localStorage
export default class extends Controller {
  static targets = ["handle"]
  static values = {
    key: { type: String, default: "resizable" },
    minWidth: { type: Number, default: 300 },
    minHeight: { type: Number, default: 300 },
    maxWidth: { type: Number, default: 0 },
    maxHeight: { type: Number, default: 0 },
  }

  connect() {
    this.loadSize()
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
  }

  disconnect() {
    document.removeEventListener("mousemove", this.onMouseMove)
    document.removeEventListener("mouseup", this.onMouseUp)
  }

  startResize(event) {
    event.preventDefault()
    this.isResizing = true
    this.startX = event.clientX
    this.startY = event.clientY
    this.startWidth = this.element.offsetWidth
    this.startHeight = this.element.offsetHeight

    document.addEventListener("mousemove", this.onMouseMove)
    document.addEventListener("mouseup", this.onMouseUp)
  }

  onMouseMove(event) {
    if (!this.isResizing) return

    // Calculate delta (inverted because we're resizing from top-left)
    const deltaX = this.startX - event.clientX
    const deltaY = this.startY - event.clientY

    // Calculate new dimensions (use Infinity if max is 0 or not set)
    const maxWidth = this.maxWidthValue || Infinity
    const maxHeight = this.maxHeightValue || Infinity

    const newWidth = Math.min(
      Math.max(this.startWidth + deltaX, this.minWidthValue),
      maxWidth,
    )
    const newHeight = Math.min(
      Math.max(this.startHeight + deltaY, this.minHeightValue),
      maxHeight,
    )

    this.element.style.width = `${newWidth}px`
    this.element.style.height = `${newHeight}px`
  }

  onMouseUp() {
    this.isResizing = false
    document.removeEventListener("mousemove", this.onMouseMove)
    document.removeEventListener("mouseup", this.onMouseUp)
    this.saveSize()
  }

  loadSize() {
    const saved = localStorage.getItem(this.storageKey)
    if (saved) {
      try {
        const { width, height } = JSON.parse(saved)
        if (width) this.element.style.width = `${width}px`
        if (height) this.element.style.height = `${height}px`
      } catch {
        // Ignore invalid JSON
      }
    }
  }

  saveSize() {
    const { width, height } = this.element.getBoundingClientRect()
    localStorage.setItem(this.storageKey, JSON.stringify({ width, height }))
  }

  get storageKey() {
    return `resizable_${this.keyValue}`
  }
}
